import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiCheckCircle, FiTool, FiArrowRight, FiFileText, FiUsers, FiClock, FiBox, FiClipboard, FiBarChart2, FiFolder, FiShield, FiHome, FiBriefcase, FiCheckSquare, FiMonitor, FiSettings, FiKey, FiChevronRight, FiDollarSign, FiCalendar, FiPackage, FiArrowLeft } from 'react-icons/fi';

const workflow = [
  { icon: FiBell, color: '#f59e0b', title: 'Create Notification', desc: 'Any user submits a notification (44xxxxxxx) describing the work needed, with type, priority, asset, and attachments.' },
  { icon: FiUsers, color: '#8b5cf6', title: 'Supervisor Review', desc: 'The notification appears in the Supervisor Panel under Workflow Approvals for review.' },
  { icon: FiCheckCircle, color: '#16a34a', title: 'Approve or Reject', desc: 'Supervisor approves (converts to a Work Order with 41/42/43 numbering) or rejects (deletes the notification).' },
  { icon: FiTool, color: '#2563eb', title: 'Work Order Created', desc: 'On approval, a full work order is created with operations, assignments, and scheduling.' },
];

const ROLES = [
  {
    key: 'team',
    title: 'Team Members',
    desc: 'Day-to-day operations — creating notifications, confirming time, managing your work',
    icon: FiUsers,
    color: '#2563eb',
    sections: [
      {
        title: 'Dashboard', icon: FiHome, items: [
          'See your key metrics: active jobs, completed this week, overdue, and total assets.',
          'View your working week progress bar showing confirmed vs total operations.',
          'Cost overview shows total spend this month with labour, parts, and services breakdown.',
          'Filter cost data by month using the date picker. Pie chart shows the cost split.',
        ]
      },
      {
        title: 'My Work', icon: FiBriefcase, items: [
          'View all work orders and operations assigned to you for the current week.',
          'Toggle "Show Completed" to include finished work. Overdue items are highlighted in red.',
          'Awaiting Parts section shows jobs with pending materials — see what\'s ordered, delivered, and installed with dates.',
          'Confirm time directly from your work list by clicking the Time Confirmation button.',
        ]
      },
      {
        title: 'Notifications', icon: FiBell, items: [
          'Create a notification from the Work Orders page or from any Asset detail page.',
          'Notifications include: title, description, type (PM01/PM02/PM03), priority, asset, due date, estimated time, and file attachments.',
          'Once submitted, notifications go to your supervisor for approval.',
          'You can add multiple planned operations with individual dates and time estimates.',
          'Sub notifications can be created from any work order, inheriting the parent asset.',
        ]
      },
      {
        title: 'Work Orders', icon: FiTool, items: [
          'Work orders flow through: New → In Progress → Complete → TECO.',
          'Each work order has operations, time confirmations, file attachments, comments, and history.',
          'Materials section tracks parts with checkbox stages: Ordered → Delivered → Installed. Each step asks for a date.',
          'Use the search bar to filter by WO number, name, asset name, or asset tag.',
          'Confirmation badges: NEW (no time), PCNF (partially confirmed), CNF (all confirmed).',
        ]
      },
      {
        title: 'Time Confirmations', icon: FiClock, items: [
          'Confirm actual hours spent on each work order operation.',
          'Mark as "Final Confirmation" to set the operation to Complete status.',
          'You can only confirm time on operations assigned to you within the current working week.',
          'Time data feeds into capacity planning and cost tracking.',
        ]
      },
      {
        title: 'Assets', icon: FiBox, items: [
          'Browse and search all assets by Asset ID, name, or description.',
          'View work order history, upload documents and photos per asset.',
          'Create notifications directly from an asset detail page.',
          'Track location, supplier, manufacturer, model, and custom fields.',
        ]
      },
      {
        title: 'Personal To Do Lists', icon: FiCheckSquare, items: [
          'Create personal task lists with named categories to organise by topic.',
          'Add, star, and complete items. Starred items appear at the top.',
          'Collapse and expand lists. Lists can only be deleted once all items are complete.',
        ]
      },
      {
        title: 'Settings', icon: FiSettings, items: [
          'Update your profile: name, email, phone, and avatar.',
          'Change your password. Set your preferred language from 12 supported languages.',
          'Configure notification preferences and display options.',
        ]
      },
    ]
  },
  {
    key: 'supervisor',
    title: 'Supervisors',
    desc: 'Team oversight — approvals, scheduling, capacity planning, weekly pre-reads',
    icon: FiClipboard,
    color: '#8b5cf6',
    sections: [
      {
        title: 'Workflow Approvals', icon: FiBell, items: [
          'Review and approve/reject pending notifications. Click any card to expand full details.',
          'The approval calendar shows daily capacity with colour coding: green (available), amber (nearing full), red (full), purple (blocked), yellow (off duty).',
          'Push priority notifications bypass off-duty, blocked, and over-capacity restrictions.',
          'Multiple operations can be added at once — each sent individually for approval.',
        ]
      },
      {
        title: 'Work Order Management', icon: FiTool, items: [
          'Overdue Operations: View operations past their due date. Reschedule or assign directly.',
          'Unassigned Jobs: Active work orders with no one assigned. Click Assign to allocate.',
          'Jobs Ready for TECO: Completed jobs where all operations are confirmed and materials complete. Sign to TECO.',
          'Awaiting Parts/Service: Jobs with incomplete materials. See exactly what\'s completed and outstanding with dates.',
        ]
      },
      {
        title: 'Weekly Pre-Read', icon: FiFileText, items: [
          'Review all team operations for the next week, grouped by day.',
          'Each day shows a capacity bar with allocated vs available hours and per-person breakdown.',
          'Click "Change Date" to move an operation — this records history on the work order.',
          'Click "Fill Time" on days with spare capacity to bring forward jobs from future weeks.',
          'Filter available jobs by type (PM01, PM02, PM03) in the bring-forward modal.',
          'Add supervisor notes for the week (e.g. focus areas, reminders).',
          'Click "Submit Pre-Read" to freeze the plan — once submitted it cannot be changed.',
          'Export to PDF for printing at toolbox meetings. View history of past pre-reads.',
          'Email and notification reminder sent every Monday when next week has planned work.',
        ]
      },
      {
        title: 'Team Capacity', icon: FiBarChart2, items: [
          'View team workload across weeks with daily capacity cells per team member.',
          'Click a day to drill down — see each job with hours, priority, and options to View WO, History, or Reschedule.',
          'Day names always show alongside OFF/BLK status so you can tell which day it is.',
          'Reschedule operations directly from the capacity view with reason and comment.',
        ]
      },
      {
        title: 'Cost Breakdown (Insights)', icon: FiDollarSign, items: [
          'View total cost, labour, materials, external labour, and hours for your team.',
          'Filter by period: This Week, This Month, Quarter, or Year.',
          'Pie chart shows the cost split. Bar chart shows labour cost by WO type.',
          'Top 5 costliest work orders listed with labour and material breakdown.',
          'Costs are calculated from time confirmations × hourly rates + material costs.',
        ]
      },
      {
        title: 'My Team', icon: FiUsers, items: [
          'View all team members with their capacity calendars.',
          'Manage team blocks (holidays, training, etc.) that prevent scheduling.',
          'See who is off duty, blocked, or overbooked on any given day.',
        ]
      },
      {
        title: 'Cast to TV', icon: FiMonitor, items: [
          'Launch a full-screen display with rotating slides for workshop screens.',
          'Shows: Welcome, Metrics, This Week\'s Jobs, Upcoming Work, Completed Work, and Cost Overview.',
          'Auto-advances every 10 seconds. Data refreshes every 60 seconds.',
          'Cost slide shows total spend with breakdown bars for labour, parts, services, and external labour.',
        ]
      },
    ]
  },
  {
    key: 'admin',
    title: 'Server Admins',
    desc: 'System configuration — users, teams, templates, authentication, site settings',
    icon: FiShield,
    color: '#dc2626',
    sections: [
      {
        title: 'User Management', icon: FiUsers, items: [
          'Create, edit, and disable user accounts. Set roles, teams, and supervisor assignments.',
          'Set hourly rates on user profiles for cost tracking.',
          'Configure working hours per day (Mon-Sun) for capacity planning.',
          'Assign KPTs (Key Permission Types) to grant specific permissions.',
        ]
      },
      {
        title: 'KPT (Key Permission Types)', icon: FiKey, items: [
          'Create named permission groups in the Admin Panel under the KPT tab.',
          'Permissions include: Approve Work Orders, Reschedule, TECO, Manage Team, Create/Time Confirm Notifications, Licences, Projects.',
          'Assign users to KPTs. KPTs cannot be deleted while users are assigned.',
          'KPT badges display on user profiles and team org charts.',
        ]
      },
      {
        title: 'Team Management', icon: FiUsers, items: [
          'Create teams, assign members, set supervision hierarchy.',
          'Org chart shows all teams with members and KPT assignments.',
          'Transfer requests are created when moving users between teams.',
          'Transfers require approval from the user\'s current supervisor.',
        ]
      },
      {
        title: 'PM02 Templates', icon: FiCalendar, items: [
          'Create recurring work order templates with scheduling frequencies.',
          'Assign templates to assets. Operations auto-generate on schedule.',
          'Sequence mode chains operations — each auto-triggers the next on completion.',
          'Legacy work orders can be linked to templates for historical tracking.',
        ]
      },
      {
        title: 'Reports & Export', icon: FiBarChart2, items: [
          'Generate Work Orders, Asset Inventory, or Time Summary reports.',
          'Apply filters: date range, status, type, assigned user, asset, category.',
          'Download as PDF (red button) or Excel (green button).',
          'Metrics view shows weekly/monthly/yearly adherence charts with target line.',
        ]
      },
      {
        title: 'Active Directory', icon: FiShield, items: [
          'Three modes: Standalone (email/password), Active Directory (domain), or Hybrid (both).',
          'Configure AD with domain server address, service account credentials.',
          'Select allowed AD groups. Only members of selected groups can sign in.',
          'Auto-import bulk imports all users from allowed groups.',
          'User details sync from AD every 2 minutes. Removed users lose access immediately.',
        ]
      },
      {
        title: 'Licences', icon: FiKey, items: [
          'Track software and equipment licences linked to assets.',
          'View, create, and manage licences. KPT permissions control access.',
        ]
      },
      {
        title: 'Projects', icon: FiFolder, items: [
          'Kanban board view with drag-and-drop between To Do, Planned, In Progress, Complete.',
          'Add members with roles: Owner, Moderator, Contributor, View Only.',
          'Track completion with progress bars. Set deadlines and assignees.',
        ]
      },
      {
        title: 'Site Settings', icon: FiSettings, items: [
          'Configure site name, SMTP email settings, and notification schedules.',
          'Set daily/weekly email send times and adherence report schedules.',
          'Configure adherence target percentage for metrics reporting.',
        ]
      },
    ]
  },
];

export default function HelpCentre() {
  const [selectedRole, setSelectedRole] = useState(null);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Help Centre</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 24px' }}>Learn how the system works — choose your role to get started</p>
      </motion.div>

      {/* Notification → Work Order Workflow (always visible) */}
      {!selectedRole && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', padding: 28, marginBottom: 28, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 20px' }}>
            <FiFileText size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            How Work Orders Are Created
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {workflow.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${step.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <step.icon size={20} color={step.color} />
                  </div>
                  {i < workflow.length - 1 && <div style={{ width: 2, height: 32, background: 'var(--border)', margin: '4px 0' }} />}
                </div>
                <div style={{ paddingTop: 4, paddingBottom: i < workflow.length - 1 ? 16 : 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                    <span style={{ color: step.color, marginRight: 8 }}>Step {i + 1}</span>{step.title}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24, padding: '16px 20px', borderRadius: 12, background: 'var(--bg-primary)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiBell size={16} color="#f59e0b" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>Notification</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(44xxxxxxx)</span>
            </div>
            <FiArrowRight size={16} color="var(--text-muted)" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCheckCircle size={16} color="#16a34a" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>Approved</span>
            </div>
            <FiArrowRight size={16} color="var(--text-muted)" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiTool size={16} color="#2563eb" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>Work Order</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(41/42/43xxxxxxx)</span>
            </div>
          </div>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {!selectedRole ? (
          /* Role selection cards */
          <motion.div key="roles" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            {ROLES.map((role, i) => (
              <motion.div key={role.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
                onClick={() => setSelectedRole(role.key)}
                whileHover={{ scale: 1.02, y: -4 }}
                style={{
                  background: 'var(--bg-card)', borderRadius: 16, border: `2px solid var(--border)`,
                  padding: 28, cursor: 'pointer', transition: 'border-color 0.2s', position: 'relative', overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: `${role.color}08` }} />
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${role.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <role.icon size={24} color={role.color} />
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>{role.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 16 }}>{role.desc}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: role.color }}>
                  {role.sections.length} topics <FiChevronRight size={14} />
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          /* Selected role content */
          <motion.div key="content" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            {(() => {
              const role = ROLES.find(r => r.key === selectedRole);
              if (!role) return null;
              return (
                <>
                  <button onClick={() => setSelectedRole(null)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                    <FiArrowLeft size={14} /> Back to roles
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 14, background: `${role.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <role.icon size={24} color={role.color} />
                    </div>
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{role.title}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{role.desc}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
                    {role.sections.map((section, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: 22 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${role.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <section.icon size={16} color={role.color} />
                          </div>
                          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{section.title}</h3>
                        </div>
                        <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {section.items.map((item, j) => (
                            <li key={j} style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item}</li>
                          ))}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                </>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
